import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { DEFAULT_PARTNER_ORG_ID } from "@/constants/system";

interface OrgInfo {
  deviceOrgId: string | null;
  deviceOrgName: string | null;
  groupOrgId: string | null;
  groupOrgName: string | null;
}

interface CrossOrgCheckResult {
  isBlocked: boolean;
  orgInfo: OrgInfo | null;
}

export const useCrossOrgAssignmentCheck = () => {
  const { role } = useSimpleRole();
  const isSuperuser = role === 'superuser';
  const [isChecking, setIsChecking] = useState(false);

  const checkCrossOrgAssignment = useCallback(async (
    deviceId: string,
    groupId: string | null
  ): Promise<CrossOrgCheckResult> => {
    // Only check for superusers - admins are already scoped
    if (!isSuperuser) {
      return { isBlocked: false, orgInfo: null };
    }

    // Unassigning from group - always allowed
    if (!groupId) {
      return { isBlocked: false, orgInfo: null };
    }

    setIsChecking(true);

    try {
      // Get group's organization
      const { data: groupData } = await supabase
        .from("device_groups")
        .select("organization_id, organizations(name)")
        .eq("id", groupId)
        .single();

      // Get device's organization via device_assignments -> user_roles
      const { data: assignmentData } = await supabase
        .from("device_assignments")
        .select(`
          user_id,
          user_roles!inner(organization_id, organizations(name))
        `)
        .eq("device_id", deviceId)
        .limit(1)
        .maybeSingle();

      const groupOrgId = groupData?.organization_id || null;
      const groupOrgName = (groupData?.organizations as any)?.name || null;
      
      const deviceOrgId = (assignmentData?.user_roles as any)?.organization_id || null;
      const deviceOrgName = (assignmentData?.user_roles as any)?.organizations?.name || null;

      const orgInfo: OrgInfo = {
        deviceOrgId,
        deviceOrgName,
        groupOrgId,
        groupOrgName,
      };

      // Block if orgs don't match (cross-org assignment)
      const isBlocked = deviceOrgId !== groupOrgId;

      return { isBlocked, orgInfo };
    } catch (error) {
      console.error("Error checking cross-org assignment:", error);
      // On error, allow the assignment (fail open)
      return { isBlocked: false, orgInfo: null };
    } finally {
      setIsChecking(false);
    }
  }, [isSuperuser]);

  // Helper to get device's organization ID
  const getDeviceOrganizationId = useCallback(async (deviceId: string): Promise<string | null> => {
    try {
      const { data: assignmentData } = await supabase
        .from("device_assignments")
        .select(`
          user_id,
          user_roles!inner(organization_id)
        `)
        .eq("device_id", deviceId)
        .limit(1)
        .maybeSingle();

      return (assignmentData?.user_roles as any)?.organization_id || null;
    } catch (error) {
      console.error("Error getting device organization:", error);
      return null;
    }
  }, []);

  // Check if device belongs to Snapshot Live
  const isSnapshotLiveDevice = useCallback(async (deviceId: string): Promise<boolean> => {
    const deviceOrgId = await getDeviceOrganizationId(deviceId);
    return deviceOrgId === DEFAULT_PARTNER_ORG_ID;
  }, [getDeviceOrganizationId]);

  return {
    checkCrossOrgAssignment,
    getDeviceOrganizationId,
    isSnapshotLiveDevice,
    isChecking,
    isSuperuser,
  };
};
